import React from 'react';
import { OffboardingReport as ReportType } from '../../types';

interface OffboardingReportProps {
  report: ReportType;
  onExportPDF: () => void;
  onReset: () => void;
}

/**
 * Rapport final de l'offboarding
 * Affiche le statut de chaque action avec possibilité d'export PDF
 */
const OffboardingReport: React.FC<OffboardingReportProps> = ({
  report,
  onExportPDF,
  onReset,
}) => {
  const successCount = report.actions.filter((a) => a.status === 'success').length;
  const errorCount = report.actions.filter((a) => a.status === 'error').length;
  const skippedCount = report.actions.filter((a) => a.status === 'skipped').length;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="bg-white border border-ms-gray-200 rounded-lg" id="offboarding-report">
      {/* En-tête du rapport */}
      <div className="px-6 py-4 border-b border-ms-gray-200 bg-ms-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ms-gray-800">
              Rapport d'offboarding
            </h3>
            <p className="text-sm text-ms-gray-500 mt-1">
              {report.targetUser.displayName} ({report.targetUser.mail})
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-ms-blue text-white rounded-md hover:bg-ms-blue-dark transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
              </svg>
              Exporter PDF
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2 text-ms-gray-600 bg-white border border-ms-gray-300 rounded-md hover:bg-ms-gray-50 transition-colors text-sm"
            >
              Nouvel offboarding
            </button>
          </div>
        </div>
      </div>

      {/* Métadonnées */}
      <div className="px-6 py-3 border-b border-ms-gray-100 flex gap-6 text-sm">
        <div>
          <span className="text-ms-gray-400">Exécuté par : </span>
          <span className="text-ms-gray-700 font-medium">{report.executedBy}</span>
        </div>
        <div>
          <span className="text-ms-gray-400">Date : </span>
          <span className="text-ms-gray-700">{formatDate(report.executedAt)}</span>
        </div>
      </div>

      {/* Résumé */}
      <div className="px-6 py-3 border-b border-ms-gray-200 flex gap-6">
        <div className="flex items-center gap-1.5">
          <span className="text-ms-green text-lg">&#10003;</span>
          <span className="text-sm text-ms-gray-600">{successCount} réussi(s)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-ms-red text-lg">&#10007;</span>
          <span className="text-sm text-ms-gray-600">{errorCount} erreur(s)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-ms-gray-400">&#9197;&#65039;</span>
          <span className="text-sm text-ms-gray-600">{skippedCount} ignoré(s)</span>
        </div>
      </div>

      {/* Détail des actions */}
      <div className="divide-y divide-ms-gray-100">
        {report.actions.map((action) => (
          <div key={action.id} className="px-6 py-3 flex items-center gap-4">
            <div className="w-6 text-center">
              {action.status === 'success' && <span className="text-ms-green">&#10003;</span>}
              {action.status === 'error' && <span className="text-ms-red">&#10007;</span>}
              {action.status === 'skipped' && <span className="text-ms-gray-400">&#9197;&#65039;</span>}
            </div>
            <div className="flex-1">
              <p className="text-sm text-ms-gray-800">{action.label}</p>
              {action.errorMessage && (
                <p className="text-xs text-ms-red mt-0.5">{action.errorMessage}</p>
              )}
              {action.infoMessage && (
                <p className="text-xs text-ms-gray-500 mt-0.5">{action.infoMessage}</p>
              )}
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                action.status === 'success'
                  ? 'bg-green-100 text-green-700'
                  : action.status === 'error'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {action.status === 'success' ? 'Succès' : action.status === 'error' ? 'Erreur' : 'Ignoré'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OffboardingReport;
